import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import SEOHead from '../components/SEOHead';
import BookPagination from '../components/BookPagination';
import { bookPath } from '../lib/slug';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Search, 
  BookOpen, 
  Target, 
  Users, 
  Star,
  Filter,
  Clock,
  Trophy,
  Sparkles,
  BookPlus,
  MessageSquare
} from 'lucide-react';
import { bookService } from '../services/bookService';
import { quizService } from '../services/quizService';
import { useToast } from '../hooks/use-toast';

const HOME_PAGE_SIZE = 24;

const Home = () => {
  const { toast } = useToast();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [genreFilter, setGenreFilter] = useState('');
  const [genres, setGenres] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalBooks, setTotalBooks] = useState(0);
  const [featuredBook, setFeaturedBook] = useState(null);
  const [featuredQuiz, setFeaturedQuiz] = useState(null);
  const [totalQuizzes, setTotalQuizzes] = useState(0);

  // Fetch genres, quiz count and today's featured picks once on mount
  useEffect(() => {
    bookService.getGenres()
      .then(setGenres)
      .catch(() => {});
    quizService.getQuizCount()
      .then(({ count }) => setTotalQuizzes(count))
      .catch(() => {});
    bookService.getFeaturedBook()
      .then(setFeaturedBook)
      .catch(() => {});
    quizService.getFeaturedQuiz()
      .then(setFeaturedQuiz)
      .catch(() => {});
  }, []);

  // Debounce search — reset to page 1 when search changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Genre change handler — resets to page 1
  const handleGenreChange = (value) => {
    setGenreFilter(value);
    setCurrentPage(1);
  };

  // Fetch books whenever page, debounced search, or genre changes
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = {
          skip: (currentPage - 1) * HOME_PAGE_SIZE,
          limit: HOME_PAGE_SIZE,
          sort: 'popular',
        };
        if (debouncedSearch) params.search = debouncedSearch;
        if (genreFilter) params.genre = genreFilter;

        const countParams = {};
        if (debouncedSearch) countParams.search = debouncedSearch;
        if (genreFilter) countParams.genre = genreFilter;

        const [booksData, { count }] = await Promise.all([
          bookService.getBooks(params),
          bookService.getBookCount(countParams),
        ]);
        if (!cancelled) {
          setBooks(booksData);
          setTotalBooks(count);
        }
      } catch (error) {
        if (!cancelled) {
          toast({ title: "Error", description: "Failed to load books.", variant: "destructive" });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [currentPage, debouncedSearch, genreFilter]);

  const totalPages = Math.ceil(totalBooks / HOME_PAGE_SIZE);

  // Structured data for homepage
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "BookQuiz",
    "url": window.location.origin,
    "description": "Interactive book quizzes powered by AI for literature enthusiasts and students",
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${window.location.origin}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    },
    "mainEntity": {
      "@type": "ItemList",
      "name": "Featured Books",
      "numberOfItems": totalBooks,
      "itemListElement": books.slice(0, 10).map((book, index) => ({
        "@type": "Book",
        "position": index + 1,
        "name": book.title,
        "author": {
          "@type": "Person",
          "name": book.author
        },
        "genre": book.genre,
        "description": book.description,
        "url": `${window.location.origin}${bookPath(book)}`
      }))
    }
  };

  return (
    <Layout>
      <SEOHead 
        title="BookQuiz - Interactive Book Knowledge Testing & Book Quizzes"
        description="Test your knowledge of classic and modern literature with AI-generated quizzes. Discover new books, challenge yourself, and deepen your reading comprehension with interactive book quizzes."
        keywords="book quiz, literature quiz, reading test, book knowledge, AI quizzes, interactive reading, book comprehension, literature test, reading challenge"
        url="/"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-purple-50 py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Test Your Book Knowledge with{' '}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Interactive Quizzes
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
              Discover the joy of reading through interactive quizzes. Challenge yourself with 
              thoughtful questions for your favorite books and authors.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <a href="#books">
                <Button className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-lg px-8 py-3 h-auto">
                  <BookOpen className="h-5 w-5 mr-2" />
                  Start Exploring Books
                </Button>
              </a>
              <Link to="/suggest-book">
                <Button variant="outline" className="w-full sm:w-auto border-2 text-lg px-8 py-3 h-auto">
                  <BookPlus className="h-5 w-5 mr-2" />
                  Suggest a Book
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-8">
              <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 md:p-6 shadow-lg">
                <BookOpen className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                <div className="text-2xl md:text-3xl font-bold text-gray-900">{totalBooks.toLocaleString()}</div>
                <div className="text-sm text-gray-600">Books Available</div>
              </div>
              <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 md:p-6 shadow-lg">
                <Target className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                <div className="text-2xl md:text-3xl font-bold text-gray-900">{totalQuizzes.toLocaleString()}</div>
                <div className="text-sm text-gray-600">Quizzes Available</div>
              </div>
              <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 md:p-6 shadow-lg">
                <Trophy className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                <div className="text-2xl md:text-3xl font-bold text-gray-900">Free</div>
                <div className="text-sm text-gray-600">Always Free</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Today */}
      {(featuredBook || featuredQuiz) && (
        <section className="py-12 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                <Sparkles className="inline h-7 w-7 text-yellow-500 mr-2" />
                Featured Today
              </h2>
              <p className="text-lg text-gray-600">A fresh pick every day — dive in!</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {featuredBook && (
                <Link
                  to={bookPath(featuredBook)}
                  className="group block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                >
                  <Card className="h-full flex flex-col bg-gradient-to-br from-blue-50 to-purple-50 border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer">
                    <CardHeader className="pb-2">
                      <Badge className="w-fit bg-blue-600 text-white mb-2">Book of the Day</Badge>
                      <div className="flex gap-4 items-start">
                        <img
                          src={featuredBook.cover_url || featuredBook.cover_image || 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=300&h=400&fit=crop&q=80'}
                          alt={`${featuredBook.title} book cover`}
                          className="w-20 h-28 object-cover rounded-lg shadow-md"
                          loading="lazy"
                        />
                        <div>
                          <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                            {featuredBook.title}
                          </CardTitle>
                          <p className="text-gray-600 mt-1">by {featuredBook.author}</p>
                          <Badge className="mt-2 bg-white/80 text-gray-700">{featuredBook.genre}</Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-2 flex flex-1 flex-col">
                      <p className="text-gray-600 text-sm line-clamp-2 mb-4">{featuredBook.description}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mt-auto">
                        <span className="flex items-center">
                          <Target className="h-4 w-4 mr-1" />
                          {featuredBook.total_quizzes || 0} quizzes
                        </span>
                        <span className="flex items-center">
                          <BookOpen className="h-4 w-4 mr-1" />
                          Explore Book
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              )}

              {featuredQuiz && (
                <Link
                  to={`/quiz/${featuredQuiz.quiz.id}`}
                  className="group block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
                >
                  <Card className="h-full flex flex-col bg-gradient-to-br from-purple-50 to-pink-50 border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer">
                    <CardHeader className="pb-2">
                      <Badge className="w-fit bg-purple-600 text-white mb-2">Quiz of the Day</Badge>
                      <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-purple-600 transition-colors">
                        {featuredQuiz.quiz.title}
                      </CardTitle>
                      <p className="text-gray-600 mt-1">from {featuredQuiz.book_title}</p>
                    </CardHeader>
                    <CardContent className="pt-2 flex flex-1 flex-col">
                      <div className="flex items-center gap-2 mb-4">
                        <Badge className="bg-white/80 text-gray-700">{featuredQuiz.quiz.difficulty}</Badge>
                        <Badge className="bg-white/80 text-gray-700">
                          {featuredQuiz.quiz.questions_count} questions
                        </Badge>
                        <Badge className="bg-white/80 text-gray-700 flex items-center">
                          <Clock className="h-3 w-3 mr-1" />
                          {featuredQuiz.quiz.time_limit} min
                        </Badge>
                      </div>
                      <div className="mt-auto">
                        <Button asChild className="w-full min-h-[44px] touch-manipulation bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transition-all group-hover:scale-105">
                          <span>
                            <Trophy className="h-4 w-4 mr-2" />
                            Take the Quiz
                          </span>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Books Section */}
      <section id="books" className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Explore Our Book Collection
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From classic literature to modern bestsellers, test your knowledge 
              with our comprehensive collection of book quizzes.
            </p>
          </div>

          {/* Search and Filter */}
          <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <Input
                  placeholder="Search books or authors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-12 text-lg"
                />
              </div>
              
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <select
                  value={genreFilter}
                  onChange={(e) => handleGenreChange(e.target.value)}
                  className="w-full pl-10 pr-4 h-12 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="">All Genres</option>
                  {genres.map(genre => (
                    <option key={genre} value={genre}>{genre}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            </div>
          ) : (
            <>
              {/* Books Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-12">
                {books.map((book) => (
                  <Link
                    key={book.id}
                    to={bookPath(book)}
                    className="group block h-full rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                  >
                    <Card className="h-full flex flex-col hover:shadow-2xl transition-all duration-300 hover:scale-105 bg-white/80 backdrop-blur-sm border-0 shadow-lg cursor-pointer">
                      <CardHeader className="pb-4">
                        <div className="aspect-[3/4] bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg mb-4 overflow-hidden relative">
                          <img
                            src={book.cover_url || book.cover_image || `https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=300&h=400&fit=crop&q=80`}
                            alt={`${book.title} book cover`}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            loading="lazy"
                          />
                          <div className="absolute top-3 right-3">
                            <Badge className="bg-white/90 text-gray-700 backdrop-blur-sm">
                              {book.genre}
                            </Badge>
                          </div>
                        </div>
                        <CardTitle className="text-lg font-bold text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
                          {book.title}
                        </CardTitle>
                        <p className="text-gray-600 font-medium">by {book.author}</p>
                      </CardHeader>
                      
                      <CardContent className="pt-0 flex flex-1 flex-col">
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                          {book.description}
                        </p>
                        
                        <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                          <div className="flex items-center">
                            <Target className="h-4 w-4 mr-1" />
                            {book.total_quizzes || 0} quizzes
                          </div>
                          <div className="flex items-center">
                            <Users className="h-4 w-4 mr-1" />
                            {book.total_attempts || 0} attempts
                          </div>
                        </div>
                        
                        <Button asChild className="w-full min-h-[44px] mt-auto touch-manipulation bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all group-hover:scale-105">
                          <span>
                            <BookOpen className="h-4 w-4 mr-2" />
                            Explore Book
                          </span>
                        </Button>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>

              <BookPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalItems={totalBooks}
                pageSize={HOME_PAGE_SIZE}
              />

              {/* No Results */}
              {books.length === 0 && !loading && (
                <div className="text-center py-20">
                  <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-2xl font-semibold text-gray-900 mb-2">No books found</h3>
                  <p className="text-gray-600 mb-6">Try adjusting your search or filter criteria</p>
                  <Link to="/suggest-book">
                    <Button variant="outline" className="min-h-[44px] touch-manipulation border-2">
                      <BookPlus className="h-4 w-4 mr-2" />
                      Suggest a Book
                    </Button>
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-blue-600 to-purple-600 py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Challenge Your Reading Knowledge?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of readers who are testing and improving their book knowledge every day.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#books">
              <Button className="w-full sm:w-auto bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-3 h-auto font-semibold">
                <BookOpen className="h-5 w-5 mr-2" />
                Browse Books Now
              </Button>
            </a>
            <Link to="/request-quiz">
              <Button variant="outline" className="w-full sm:w-auto border-2 border-white bg-transparent text-white hover:bg-white/10 text-lg px-8 py-3 h-auto">
                <MessageSquare className="h-5 w-5 mr-2" />
                Request a Quiz
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
